import type { Look } from '../types';

interface P {
  x: number;
  y: number;
  vx: number;
  vy: number;
  life: number;
  max: number;
  size: number;
  color: number;
  rot: number;
  vr: number;
  phase: number;
}

/**
 * 粒子层（Canvas 2D，加色混合）
 *   motes   光尘：自下而上缓慢飘升、闪烁
 *   petals  花雨：莲瓣自上飘落、旋转
 *   radiate 放射：自光心向外散射
 */
export class Particles {
  private ctx: CanvasRenderingContext2D;
  private ps: P[] = [];
  private sprites: HTMLCanvasElement[] = [];
  private petalSprites: HTMLCanvasElement[] = [];
  private spriteKey = '';
  private w = 1;
  private h = 1;
  private dpr = 1;
  private mode: Look['particles']['mode'] | '' = '';

  constructor(private readonly canvas: HTMLCanvasElement) {
    this.ctx = canvas.getContext('2d')!;
  }

  resize(w: number, h: number): void {
    this.dpr = Math.min(window.devicePixelRatio || 1, 2);
    this.w = w;
    this.h = h;
    this.canvas.width = Math.round(w * this.dpr);
    this.canvas.height = Math.round(h * this.dpr);
  }

  private ensureSprites(look: Look): void {
    const key = [look.colors.primary, look.colors.secondary, look.colors.accent].join();
    if (key === this.spriteKey) return;
    this.spriteKey = key;
    const colors = [look.colors.primary, look.colors.secondary, look.colors.accent];
    this.sprites = colors.map((c) => glowSprite(c));
    this.petalSprites = colors.map((c) => petalSprite(c));
  }

  private spawn(look: Look, initial: boolean): P {
    const { mode, speed, spread } = look.particles;
    const min = Math.min(this.w, this.h);
    const cx = look.halo.x * this.w;
    const cy = look.halo.y * this.h;
    const p: P = { x: 0, y: 0, vx: 0, vy: 0, life: 0, max: 1, size: 1, color: 0, rot: Math.random() * 6.28, vr: 0, phase: Math.random() * 6.28 };
    if (mode === 'motes') {
      const nearHalo = Math.random() < 0.35;
      if (nearHalo) {
        const a = Math.random() * Math.PI * 2;
        const r = (0.1 + Math.random() * 0.35 * spread) * min;
        p.x = cx + Math.cos(a) * r;
        p.y = cy + Math.sin(a) * r;
      } else {
        p.x = this.w * (0.5 + (Math.random() - 0.5) * spread * 1.1);
        p.y = initial ? Math.random() * this.h : this.h + 10;
      }
      p.vx = (Math.random() - 0.5) * 6;
      p.vy = -(8 + Math.random() * 22) * speed;
      p.max = 6 + Math.random() * 10;
      p.size = (1.5 + Math.random() * Math.random() * 6) * look.particles.size;
      p.color = Math.random() < 0.7 ? 0 : Math.random() < 0.6 ? 1 : 2;
    } else if (mode === 'petals') {
      p.x = this.w * (0.5 + (Math.random() - 0.5) * spread * 1.2);
      p.y = initial ? Math.random() * this.h : -20;
      p.vx = (Math.random() - 0.5) * 10;
      p.vy = (10 + Math.random() * 18) * speed;
      p.vr = (Math.random() - 0.5) * 1.2;
      p.max = 14 + Math.random() * 12;
      p.size = (5 + Math.random() * 7) * look.particles.size;
      p.color = Math.random() < 0.55 ? 1 : 0;
    } else {
      const a = Math.random() * Math.PI * 2;
      const r0 = look.halo.radius * min * (0.4 + Math.random() * 0.4);
      p.x = cx + Math.cos(a) * r0;
      p.y = cy + Math.sin(a) * r0;
      const v = (30 + Math.random() * 70) * speed;
      p.vx = Math.cos(a) * v;
      p.vy = Math.sin(a) * v;
      p.max = (2 + Math.random() * 4) * (0.6 + spread);
      p.size = (1 + Math.random() * 3.5) * look.particles.size;
      p.color = Math.random() < 0.6 ? 0 : Math.random() < 0.5 ? 1 : 2;
      if (initial) p.life = Math.random() * p.max;
    }
    if (initial && mode !== 'radiate') p.life = Math.random() * p.max * 0.8;
    return p;
  }

  /** drive：已乘以影响系数的音频驱动；pulse：起音/换句脉冲 */
  update(look: Look, dt: number, drive: number, pulse: number): void {
    this.ensureSprites(look);
    const cfg = look.particles;
    if (this.mode !== cfg.mode) {
      this.mode = cfg.mode;
      this.ps = [];
      for (let i = 0; i < cfg.count; i++) this.ps.push(this.spawn(look, true));
    }
    const target = Math.round(cfg.count * (1 + pulse * 0.5));
    let spawnBudget = Math.max(2, Math.round(cfg.count * dt * (0.6 + drive * 2 + pulse * 6)));
    while (this.ps.length < target && spawnBudget-- > 0) this.ps.push(this.spawn(look, false));

    const speedMul = 1 + drive * 1.6 + pulse * 1.2;
    const t = performance.now() / 1000;
    const out: P[] = [];
    for (const p of this.ps) {
      p.life += dt;
      const sway = cfg.mode === 'radiate' ? 0 : Math.sin(t * 0.8 + p.phase) * (cfg.mode === 'petals' ? 14 : 5);
      p.x += (p.vx + sway) * dt * speedMul;
      p.y += p.vy * dt * speedMul;
      p.rot += p.vr * dt;
      const alive = p.life < p.max && p.y > -40 && p.y < this.h + 40 && p.x > -40 && p.x < this.w + 40;
      if (alive) out.push(p);
    }
    // 数量减少时逐步淘汰
    this.ps = out.length > cfg.count * 1.6 ? out.slice(0, Math.round(cfg.count * 1.6)) : out;
  }

  draw(look: Look, drive: number, pulse: number): void {
    const ctx = this.ctx;
    ctx.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);
    ctx.clearRect(0, 0, this.w, this.h);
    ctx.globalCompositeOperation = 'lighter';
    const base = look.particles.intensity * (0.55 + drive * 0.9 + pulse * 0.4);
    const t = performance.now() / 1000;
    const petals = look.particles.mode === 'petals';
    for (const p of this.ps) {
      const lifeK = Math.sin(Math.min(1, p.life / p.max) * Math.PI);
      const tw = petals ? 1 : 0.6 + 0.4 * Math.sin(t * 3 + p.phase * 3);
      const a = Math.min(1, base * lifeK * tw);
      if (a <= 0.01) continue;
      ctx.globalAlpha = a;
      if (petals) {
        const s = p.size * 2;
        ctx.save();
        ctx.translate(p.x, p.y);
        ctx.rotate(p.rot);
        ctx.scale(1, 0.55 + 0.45 * Math.sin(t * 1.3 + p.phase));
        ctx.drawImage(this.petalSprites[p.color], -s, -s, s * 2, s * 2);
        ctx.restore();
      } else {
        const s = p.size * (3 + drive * 1.5);
        ctx.drawImage(this.sprites[p.color], p.x - s, p.y - s, s * 2, s * 2);
      }
    }
    ctx.globalAlpha = 1;
    ctx.globalCompositeOperation = 'source-over';
  }
}

function glowSprite(color: string): HTMLCanvasElement {
  const c = document.createElement('canvas');
  c.width = c.height = 64;
  const g = c.getContext('2d')!;
  const grd = g.createRadialGradient(32, 32, 0, 32, 32, 32);
  grd.addColorStop(0, 'rgba(255,255,255,1)');
  grd.addColorStop(0.12, color);
  grd.addColorStop(0.4, hexA(color, 0.35));
  grd.addColorStop(1, hexA(color, 0));
  g.fillStyle = grd;
  g.fillRect(0, 0, 64, 64);
  return c;
}

function petalSprite(color: string): HTMLCanvasElement {
  const c = document.createElement('canvas');
  c.width = c.height = 64;
  const g = c.getContext('2d')!;
  g.translate(32, 32);
  const grd = g.createRadialGradient(0, 6, 0, 0, 0, 28);
  grd.addColorStop(0, 'rgba(255,255,255,0.95)');
  grd.addColorStop(0.45, hexA(color, 0.75));
  grd.addColorStop(1, hexA(color, 0));
  g.fillStyle = grd;
  g.beginPath();
  // 莲瓣：上尖下圆
  g.moveTo(0, -26);
  g.bezierCurveTo(16, -12, 16, 14, 0, 22);
  g.bezierCurveTo(-16, 14, -16, -12, 0, -26);
  g.fill();
  return c;
}

function hexA(hex: string, a: number): string {
  let h = hex.replace('#', '');
  if (h.length === 3) h = h.split('').map((x) => x + x).join('');
  const n = parseInt(h.slice(0, 6), 16) || 0;
  return `rgba(${(n >> 16) & 255},${(n >> 8) & 255},${n & 255},${a})`;
}
